// src/components/CREGrid.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Edit as EditIcon,
  Cached as CachedIcon,
  ContentCopy as DuplicateIcon
} from "@mui/icons-material";

import {
  ValidationErrorModal,
  PamGrid,
  useNotification,
  useChangeTracking,
  exportToExcel,
  BooleanRenderer,
  createNumberFormatter,
  DateRenderer,
  DateTimeRenderer,
  parseFileData,
  buildHeaderMappings,
  createColumnNormalizer,
  normalizeRowKeys,
  mergeRows,
  normalizeNA,
  buildFieldTransforms,
  buildValidatorsFromFieldDefs,
  isPermissionDenied,
  getPermissionDeniedMessage,
  notifyPermissionError,
  GridConfigMenu,
  useGridConfigs,
  loadedModifiedDate,
} from '@1823-partners/core';
import { RowSelectionModule, SelectEditorModule, DateEditorModule } from 'ag-grid-community';
import { useCanWriteCre, NO_CRE_WRITE_MESSAGE } from '../permissions';
import { RichSelectModule } from 'ag-grid-enterprise';
import api, { fetchCRELoans, saveCRELoan, fetchEnrichmentData, refreshCache } from "../api";
import config from "../data/config.json";
import LoanModal from "./LoanModal";
import HistoryModal from "./HistoryModal";
import BulkEditModal from "./BulkEditModal";
import { formatDate } from "../utils";
import { validateCREList, formatValidationErrorsForDisplay, enrichLoan, enrichLoans, applyCalculations, getChoiceDisplayLabel, getRowFieldChoices } from "../utils/creUtils";
import { creWriteRow, savedCREToken, creWriteError, hasTemporaryCusip } from '../utils/creWrite';
import creFields from "../data/creFields.json";

dayjs.extend(utc);
dayjs.extend(timezone);

// Stamped by the writer on every save. A grid row's MODIFIED_DATE is the token
// the row was loaded with (or null for a new CUSIP) and is never edited locally.
const WRITER_OWNED = ['MODIFIED_BY', 'MODIFIED_DATE'];

// The fields of `edited` that differ from `base` (writer-owned fields excluded).
// The form turns blanks into null and unset booleans into false, so those are
// not changes: counting them would revert another user's value on a reload.
const sameValue = (field, left, right) => {
  const blank = (v) => v === '' || v === undefined || v === null;
  if (config.booleanFields.includes(field)) {
    const asBool = (v) => (blank(v) ? false : v === true || v === 'Y' || v === 1 || v === '1');
    return asBool(left) === asBool(right);
  }
  if (blank(left) && blank(right)) return true;
  return JSON.stringify(left) === JSON.stringify(right);
};
export const changedFields = (base, edited) => Object.fromEntries(
  Object.entries(edited).filter(([field, value]) => (
    !WRITER_OWNED.includes(field) && !sameValue(field, base[field], value)
  ))
);

const CREGrid = ({ userId }) => {
  const [editingLoan, setEditingLoan] = useState(null);
  const [loanModalOpen, setLoanModalOpen] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedLoanForHistory, setSelectedLoanForHistory] = useState(null);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [selectedRowsForBulkEdit, setSelectedRowsForBulkEdit] = useState([]);
  const [enrichmentData, setEnrichmentData] = useState(null);
  const [isEnrichmentLoading, setIsEnrichmentLoading] = useState(false);
  const gridRef = useRef(null);
  const enrichmentDataRef = useRef(null);
  const loanRequestIdRef = useRef(0);
  const enrichmentRequestIdRef = useRef(0);
  // Modal edits that have not saved yet (the change tracker only sees grid
  // cell edits), so a failed save or a reload keeps them visible.
  const pendingModalRef = useRef(new Map());
  const { showNotification } = useNotification();
  const canWriteCre = useCanWriteCre();

  // Read-only mode when viewing historical data (selectedDate < today)
  const isPreviousPeriod = useMemo(() => {
    return selectedDate.isBefore(dayjs(), 'day');
  }, [selectedDate]);

  // 1823-core hooks for change tracking
  const getRowId = useCallback((data) => data.CUSIP, []);
  const isNewRow = useCallback((data) => {
    const cusip = data.CUSIP;
    return (typeof cusip === 'string' && cusip.startsWith('new_row_')) || data._isUploadedNew === true;
  }, []);

  const {
    updatedRows,
    trackChange,
    clearChanges,
    getChangedData,
    setUpdatedRows
  } = useChangeTracking({ getRowId, isNewRow });

  // Per-user grid layout persistence. Own the hook here (rather than letting
  // GridConfigMenu instantiate it) so we can wire gridConfigs.onGridReady into
  // PamGrid's onGridReady — without that bridge a slow grid that mounts after
  // the configs response would miss the default-layout auto-apply.
  const gridConfigs = useGridConfigs(api, {
    app: 'cre',
    gridId: 'loans',
    gridApiRef: gridRef,
  });

  // GridConfigMenu is stateful (dropdown + dialog) and needs gridRef, so it
  // rides through PamGrid's customToolbarComponents slot.
  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Compose the existing STATUS filter default with the configs ready-bridge.
  const handleGridReady = useCallback((params) => {
    params.api.setFilterModel({
      STATUS: { filterType: 'number', type: 'notEqual', filter: 6 }
    });
    gridConfigs.onGridReady(params);
  }, [gridConfigs]);

  // preserveLocalChanges: keep unsaved grid, modal and new-row edits over the
  // fresh rows. adoptServerTokens: after a 409 the kept edits take the fresh
  // row's MODIFIED_DATE, so saving again is the user's deliberate re-apply.
  // Otherwise (a background refresh) an edited row keeps the MODIFIED_DATE its
  // edits were based on, so the refresh cannot turn them into a blind overwrite.
  const loadCRELoans = useCallback(async (
    preserveLocalChanges = false,
    silent = false,
    adoptServerTokens = false,
  ) => {
    const requestId = ++loanRequestIdRef.current;
    const isStale = () => requestId !== loanRequestIdRef.current;
    const requestedDateStr = selectedDate.format('YYYY-MM-DD');
    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      // As-of means the start of the *next local day*, converted to a UTC
      // instant. A UTC date-only cutoff would hide saves made late in the
      // viewer's evening and make every later save of those rows 409.
      const before = selectedDate.startOf('day').add(1, 'day').toDate().toISOString();
      const response = await fetchCRELoans(before);
      if (isStale()) return;
      const rawFormatted = response.data.map(loan => {
        const formattedLoan = {};
        Object.keys(loan).forEach(key => {
          // MODIFIED_DATE is the writer's token: keep it exactly as returned.
          formattedLoan[key] = loan[key] === null && key !== 'MODIFIED_DATE' ? '' : loan[key];
        });
        config.dateFields.forEach(field => {
          if (formattedLoan[field]) {
            formattedLoan[field] = formatDate(formattedLoan[field]);
          }
        });
        return formattedLoan;
      });
      // Apply calculations immediately (including B Note A+B balance lookup across allLoans)
      // so derived fields (DSCR, LTV, etc.) are visible even before enrichment data arrives
      const formattedData = rawFormatted.map(loan => applyCalculations(loan, [], rawFormatted));

      const withEnrichment = (rows) => {
        const currentEnrichment = enrichmentDataRef.current;
        if (!currentEnrichment) return rows;
        if (currentEnrichment._asOfDate && currentEnrichment._asOfDate !== requestedDateStr) {
          return rows;
        }
        return enrichLoans(rows, currentEnrichment);
      };

      if (preserveLocalChanges) {
        const pendingModal = pendingModalRef.current;

        setUpdatedRows(currentUpdatedRows => {
          setRowData(currentRowData => {
            const localByKey = new Map(currentRowData.map(row => [String(row.CUSIP), row]));
            const merged = formattedData.map(fresh => {
              const key = String(fresh.CUSIP);
              const changes = currentUpdatedRows[fresh.CUSIP];
              const modalEdit = pendingModal.get(key);
              if (!changes && !modalEdit) return fresh;
              const row = { ...fresh };
              Object.entries(changes || {}).forEach(([field, change]) => {
                if (!WRITER_OWNED.includes(field)) row[field] = change.newValue;
              });
              Object.entries(modalEdit || {}).forEach(([field, value]) => {
                if (!WRITER_OWNED.includes(field)) row[field] = value;
              });
              const local = localByKey.get(key);
              row.MODIFIED_DATE = adoptServerTokens || !local
                ? fresh.MODIFIED_DATE
                : local.MODIFIED_DATE;
              return row;
            });
            // Unsaved new rows stay on top with a null token. If someone else
            // created the same CUSIP meanwhile, the user's row replaces it on
            // screen and its save 409s instead of overwriting theirs.
            const newRows = currentRowData
              .filter(row => isNewRow(row))
              .map(row => ({ ...row, MODIFIED_DATE: null }));
            const newKeys = new Set(newRows.map(row => String(row.CUSIP)));
            return withEnrichment([
              ...newRows,
              ...merged.filter(row => !newKeys.has(String(row.CUSIP))),
            ]);
          });

          return currentUpdatedRows;
        });
      } else {
        // If enrichment data is already loaded, apply it immediately
        // This prevents a race condition where enrichmentData arrives before
        // CRE loans, causing the enrichment effect to skip (empty rowData)
        pendingModalRef.current.clear();
        setRowData(withEnrichment(formattedData));
        clearChanges();
      }
    } catch (err) {
      if (isStale()) return;
      console.error("Error fetching CRE loans:", err);
      if (isPermissionDenied(err)) {
        setError(getPermissionDeniedMessage(err));
      } else {
        setError("Failed to load CRE loan data. Please try again.");
      }
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  }, [selectedDate, clearChanges, setUpdatedRows, isNewRow]);

  useEffect(() => {
    if (selectedDate) {
      loadCRELoans();
    }
  }, [selectedDate, loadCRELoans]);

  // Keep enrichmentData ref in sync with state so loadCRELoans can access it
  // without adding enrichmentData to its dependency array
  useEffect(() => {
    enrichmentDataRef.current = enrichmentData;
  }, [enrichmentData]);

  // Load enrichment data when selectedDate changes
  const loadEnrichmentData = useCallback(async () => {
    const requestId = ++enrichmentRequestIdRef.current;
    const isStale = () => requestId !== enrichmentRequestIdRef.current;
    try {
      setIsEnrichmentLoading(true);
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const response = await fetchEnrichmentData(dateStr);
      if (isStale()) return;
      const data = response.data ? { ...response.data, _asOfDate: dateStr } : response.data;

      // Log warnings from server if any data sources failed
      if (data?.warnings?.length > 0) {
        console.warn('Enrichment data warnings:', data.warnings);
      }

      setEnrichmentData(data);
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to load enrichment data:', err);
      if (isPermissionDenied(err)) {
        showNotification(getPermissionDeniedMessage(err), 'warning');
      }
    } finally {
      if (!isStale()) {
        setIsEnrichmentLoading(false);
      }
    }
  }, [selectedDate, showNotification]);

  // Refresh cached reference data from server
  const handleRefreshCache = useCallback(async () => {
    const requestId = ++enrichmentRequestIdRef.current;
    const isStale = () => requestId !== enrichmentRequestIdRef.current;
    try {
      setIsEnrichmentLoading(true);
      // Clear server cache and reload
      await refreshCache(true);
      // Reload enrichment data with fresh cache
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const response = await fetchEnrichmentData(dateStr);
      if (isStale()) return;
      setEnrichmentData(response.data ? { ...response.data, _asOfDate: dateStr } : response.data);
      showNotification('Reference data refreshed successfully', 'success');
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to refresh cache:', err);
      // notifyPermissionError, not the message variant: this is a GATED write
      // (cre.loans:write), so the denial gets the in-app "Request access" button. The
      // message variant inlines a link to the Permissions app instead, which sends the
      // user out of the tab to do by hand what the dialog does in place.
      if (!notifyPermissionError(showNotification, err)) {
        showNotification('Failed to refresh reference data', 'error');
      }
    } finally {
      if (!isStale()) {
        setIsEnrichmentLoading(false);
      }
    }
  }, [selectedDate, showNotification]);

  useEffect(() => {
    if (selectedDate) {
      loadEnrichmentData();
    }
  }, [selectedDate, loadEnrichmentData]);

  // Enrich existing rows when enrichmentData becomes available
  useEffect(() => {
    if (!enrichmentData) {
      return;
    }
    const currentDateStr = selectedDate.format('YYYY-MM-DD');
    if (enrichmentData._asOfDate && enrichmentData._asOfDate !== currentDateStr) {
      return;
    }

    // Use functional update to access current rowData without adding it to dependencies
    setRowData(currentRowData => {
      if (currentRowData.length === 0) {
        return currentRowData;
      }

      const enrichedRows = enrichLoans(currentRowData, enrichmentData);

      // Apply whenever any field on any row changed
      const hasChanges = enrichedRows.some((row, idx) => {
        const original = currentRowData[idx];
        const keys = new Set([...Object.keys(row), ...Object.keys(original)]);
        for (const key of keys) {
          if (!Object.is(row[key], original[key])) return true;
        }
        return false;
      });

      if (hasChanges) {
        return enrichedRows;
      }

      return currentRowData;
    });
  }, [enrichmentData, selectedDate]);

  // === UPLOAD: 1823-core header mappings & field transforms (built once) ===
  const headerMappings = useMemo(() => {
    return buildHeaderMappings(creFields, { 'PAM_RISK_RATING': 'RISK_RATING_CURRENT' });
  }, []);

  const columnNormalizer = useMemo(() => {
    return createColumnNormalizer(headerMappings);
  }, [headerMappings]);

  const fieldTransforms = useMemo(() => {
    return buildFieldTransforms(creFields);
  }, []);

  // Build a combined validator for all fields with a `choices` array
  const validateDropdownValues = useMemo(() => {
    return buildValidatorsFromFieldDefs(creFields);
  }, []);

  // === UPLOAD HANDLER ===
  const handleUpload = useCallback(async (file) => {
    try {
      setIsLoading(true);
      const parsedData = await parseFileData(file);

      if (!parsedData || parsedData.length === 0) {
        showNotification('File must contain at least one data row', 'error');
        return;
      }

      // Normalize header names using 1823-core utilities
      const existingByCusip = new Map(rowData.map(r => [r.CUSIP, r]));
      const dropdownErrors = [];
      const normalizedRows = parsedData.map((row, idx) => {
        const mapped = normalizeRowKeys(row, columnNormalizer);
        const rawParticipation = normalizeNA(mapped.PARTICIPATION);
        const isParticipationBlank =
          rawParticipation === null ||
          rawParticipation === undefined ||
          (typeof rawParticipation === 'string' && rawParticipation.trim() === '');

        // Apply field-level transforms (normalizeNA, coerceDate, coerceNumeric)
        const transformed = {};
        Object.keys(mapped).forEach(field => {
          let val = mapped[field];
          // Handle JS Date objects from Excel parsing
          if (val instanceof Date && !isNaN(val)) {
            val = val.toISOString().split('T')[0];
          }
          // Apply 1823-core field transform if one exists for this field
          if (fieldTransforms[field]) {
            transformed[field] = fieldTransforms[field](val);
          } else {
            // For unmapped fields, still normalize N/A
            transformed[field] = normalizeNA(val);
          }
        });
        // Coerce CUSIP to string — Excel may parse purely-numeric CUSIPs as numbers,
        // which then breaks isNewRow's `.startsWith` call and matchField equality in mergeRows.
        if (transformed.CUSIP != null && typeof transformed.CUSIP !== 'string') {
          transformed.CUSIP = String(transformed.CUSIP);
        }
        // Reject non-blank unparseable PARTICIPATION cells before applying the default,
        // and preserve an existing loan's populated PARTICIPATION on partial updates.
        if (!isParticipationBlank && (transformed.PARTICIPATION === null || transformed.PARTICIPATION === undefined)) {
          const cusip = transformed.CUSIP || `Row ${idx + 1}`;
          dropdownErrors.push(
            `Row ${idx + 1} (${cusip}): PARTICIPATION value "${mapped.PARTICIPATION}" is not a valid number.`
          );
        } else if (isParticipationBlank) {
          const existingRow = transformed.CUSIP ? existingByCusip.get(transformed.CUSIP) : undefined;
          const existingHasParticipation =
            existingRow &&
            existingRow.PARTICIPATION !== null &&
            existingRow.PARTICIPATION !== undefined &&
            existingRow.PARTICIPATION !== '';
          if (!existingHasParticipation) {
            transformed.PARTICIPATION = 1;
          }
        }
        return transformed;
      });

      // Validate dropdown/choices fields against allowed values
      normalizedRows.forEach((row, idx) => {
        const err = validateDropdownValues(row, idx);
        if (err) {
          const cusip = row.CUSIP || `Row ${idx + 1}`;
          dropdownErrors.push(
            `Row ${idx + 1} (${cusip}): ${err.field} value "${err.value}" is not valid. ${err.message}`
          );
        }
      });

      if (dropdownErrors.length > 0) {
        showNotification(`Upload validation failed:\n${dropdownErrors.join('\n')}`, 'error');
        return;
      }

      // Validate that file contains all required columns
      const fileFields = new Set(Object.keys(normalizedRows[0]));
      const requiredUploadFields = ['CUSIP', 'LOAN_NAME', 'PROPERTY_TYPE', 'ENTITY', 'LOAN_TYPE'];
      const missingFields = requiredUploadFields.filter(field => !fileFields.has(field));
      if (missingFields.length > 0) {
        const displayNames = missingFields.map(f => creFields[f]?.name || f);
        showNotification(`Upload file is missing required columns: ${displayNames.join(', ')}`, 'error');
        return;
      }

      // File audit fields are not authoritative: only loaded rows supply a
      // MODIFIED_DATE, and the writer supplies MODIFIED_BY.
      const uploadedRows = normalizedRows.map(({ MODIFIED_DATE, MODIFIED_BY, ...row }) => row);
      // Use 1823-core mergeRows for upsert logic
      const result = mergeRows({
        uploadedRows,
        existingRows: rowData,
        matchField: 'CUSIP',
        mergeStrategy: 'upsert',
        onNewRow: (row, index) => {
          const cusip = row.CUSIP;
          let newRow;
          if (!cusip) {
            // No CUSIP — generate a temporary ID
            const newRowId = `new_row_${Date.now()}_${index}`;
            newRow = {
              ...row,
              CUSIP: newRowId,
              MODIFIED_DATE: null
            };
          } else {
            // New CUSIP not in grid — flag as uploaded
            newRow = {
              ...row,
              _isUploadedNew: true,
              MODIFIED_DATE: null
            };
          }
          // Apply enrichment data if available
          if (enrichmentData) {
            newRow = enrichLoan(newRow, enrichmentData, rowData);
          }
          return newRow;
        },
      });

      // Track changes for updated rows using the fieldChanges from mergeRows
      Object.entries(result.fieldChanges).forEach(([cusip, changes]) => {
        const existingRow = rowData.find(r => r.CUSIP === cusip);
        if (existingRow) {
          Object.entries(changes).forEach(([field, { oldValue, newValue }]) => {
            if (field !== 'CUSIP') {
              trackChange(existingRow, field, oldValue, newValue);
            }
          });
        }
      });

      // The file never supplies a token: matched rows keep the MODIFIED_DATE
      // they were loaded with (mergeRows leaves it alone, since it was
      // stripped from the upload), and new keys send null.
      const finalData = result.mergedData.map(row => (
        isNewRow(row) ? { ...row, MODIFIED_DATE: null } : row
      ));

      setRowData(finalData);

      const message = [];
      if (result.newCount > 0) {
        message.push(`${result.newCount} new row(s) added`);
      }
      if (result.updatedCount > 0) {
        message.push(`${result.updatedCount} existing row(s) updated`);
      }

      if (message.length > 0) {
        showNotification(`Upload successful: ${message.join(', ')}. Click Save to persist changes.`, 'success');
      } else {
        showNotification('No changes detected in uploaded file', 'warning');
      }

    } catch (err) {
      console.error('Error processing upload:', err);
      showNotification(`Error processing file: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [rowData, showNotification, trackChange, enrichmentData, columnNormalizer, fieldTransforms, validateDropdownValues, isNewRow]);

  // === ACTION HANDLERS ===
  const handleEditLoan = useCallback((loanData) => {
    const currentRowData = rowData.find(row => row.CUSIP === loanData.CUSIP);
    const dataToEdit = currentRowData || loanData;
    setEditingLoan(dataToEdit);
    setLoanModalOpen(true);
  }, [rowData]);

  const handleWriteFailure = useCallback(async (err, fallback) => {
    if (notifyPermissionError(showNotification, err)) return;
    const { message, reload } = creWriteError(err, fallback);
    // 409 or outcome unknown: reload, keeping every unsaved edit visible.
    // Only a 409 lets the kept edits take the fresh MODIFIED_DATE (saving
    // again is the user's deliberate re-apply). After an unknown outcome
    // they keep their loaded token: if the first save did commit, saving
    // again 409s instead of writing twice. Never retried automatically.
    if (reload) {
      await loadCRELoans(true, true, err?.response?.status === 409);
    }
    showNotification(message, 'error');
  }, [loadCRELoans, showNotification]);

  const handleSaveEditedLoan = useCallback(async (editedData) => {
    const existingRow = rowData.find(row => row.CUSIP === editedData.CUSIP);
    // The grid row carries the token it was loaded with; the form's copy
    // of MODIFIED_DATE is ignored.
    const loadedRow = existingRow || { MODIFIED_DATE: null };
    const mergedData = existingRow ? { ...existingRow, ...editedData } : editedData;
    const npn = enrichmentData?.npn || [];
    const allLoansForCalc = rowData.map(row => row.CUSIP === editedData.CUSIP ? mergedData : row);
    const calculatedData = applyCalculations(mergedData, npn, allLoansForCalc);
    const formattedEntry = creWriteRow(calculatedData, loadedRow);
    // Keep the edited form values visible if the save fails, including after
    // a 409 reload. Modal edits are not tracked by useChangeTracking.
    // Only the fields the form actually changed: after a 409 they are laid
    // over the fresh row, so a whole-form copy would silently revert other
    // users' changes to fields this user never touched.
    pendingModalRef.current.set(
      String(editedData.CUSIP),
      changedFields(existingRow || {}, editedData),
    );
    setRowData(rows => rows.map(row => row.CUSIP === editedData.CUSIP
      ? { ...calculatedData, MODIFIED_DATE: loadedModifiedDate(loadedRow) }
      : row));
    try {
      setIsSaving(true);
      const response = await saveCRELoan([formattedEntry]);
      const adopted = savedCREToken(calculatedData, response.data.modified_date);
      setRowData(rows => rows.map(row => row.CUSIP === editedData.CUSIP ? adopted : row));
      pendingModalRef.current.delete(String(editedData.CUSIP));
      clearChanges([editedData.CUSIP]);
      // A save of one loan must not wipe edits to other loans. Reload silently
      // and merge any pending grid edits on top of the server response.
      await loadCRELoans(true, true);
      showNotification('CRE loan entry saved successfully', 'success');
    } catch (err) {
      console.error('Error saving CRE loan entry:', err);
      await handleWriteFailure(err, 'Failed to save CRE loan entry');
    } finally {
      setIsSaving(false);
    }
  }, [rowData, enrichmentData, showNotification, loadCRELoans, handleWriteFailure, clearChanges]);

  const handleSaveNewLoan = useCallback(async (newLoanData) => {
    const npn = enrichmentData?.npn || [];
    const calculatedData = applyCalculations(newLoanData, npn, [newLoanData, ...rowData]);
    if (hasTemporaryCusip([calculatedData])) {
      showNotification('Enter a CUSIP before saving this new loan', 'error');
      return;
    }
    const formattedEntry = creWriteRow(calculatedData, { MODIFIED_DATE: null });
    const pending = { ...calculatedData, MODIFIED_DATE: null, _isUploadedNew: true };
    pendingModalRef.current.set(String(pending.CUSIP), pending);
    setRowData(rows => [pending, ...rows.filter(row => row.CUSIP !== pending.CUSIP)]);
    try {
      setIsSaving(true);
      const response = await saveCRELoan([formattedEntry]);
      const adopted = savedCREToken(calculatedData, response.data.modified_date);
      pendingModalRef.current.delete(String(adopted.CUSIP));
      setRowData(rows => rows.map(row => row.CUSIP === adopted.CUSIP ? adopted : row));
      await loadCRELoans(true, true);
      showNotification('New CRE loan entry created successfully', 'success');
    } catch (err) {
      console.error('Error creating CRE loan entry:', err);
      await handleWriteFailure(err, 'Failed to create CRE loan entry');
    } finally {
      setIsSaving(false);
    }
  }, [rowData, enrichmentData, showNotification, loadCRELoans, handleWriteFailure]);

  // Duplicate opens the loan form prefilled from the row, with a generated
  // CUSIP the user can save. (A grid row with a placeholder new_row_* key
  // could never be saved: CUSIP is read-only in the grid and the form.)
  const [duplicateTemplate, setDuplicateTemplate] = useState(null);
  const handleDuplicateLoan = useCallback((loanData) => {
    const { MODIFIED_DATE, MODIFIED_BY, ...template } = loanData;
    setEditingLoan(null);
    setDuplicateTemplate(enrichmentData ? enrichLoan(template, enrichmentData, rowData) : template);
    setLoanModalOpen(true);
  }, [enrichmentData, rowData]);

  const handleViewHistory = useCallback((loanData) => {
    setSelectedLoanForHistory(loanData);
    setHistoryModalOpen(true);
  }, []);

  const handleCellValueChanged = useCallback((params) => {
    const { data, colDef, oldValue, newValue, api } = params;
    const cusip = data.CUSIP;
    const field = colDef.field;

    if (oldValue === newValue || field === 'MODIFIED_DATE' || field === 'MODIFIED_BY') return;

    trackChange(data, field, oldValue, newValue);

    const calcTriggerFields = [
      'CURRENT_YEAR_NOI', 'DEBT_SERVICE', 'CURRENT_BALANCE', 'INTEREST_RATE',
      'ANNUAL_SERVICING_FEE', 'PARTICIPATION', 'AS_IS_APPRAISED_VALUE',
      'PROPERTY_TYPE', 'LOAN_CATEGORY', 'PRIOR_YEAR_NOI', 'SECOND_PRIOR_YEAR_NOI',
      'ORIGINATION_DATE', 'FULLY_EXTENDED_MATURITY_DATE', 'AMORTIZATION_TYPE',
      'LOAN_TYPE', 'LOAN_NAME', 'MATURITY'
    ];

    // A cell edit changes only data fields. The loaded MODIFIED_DATE stays
    // byte-for-byte as received from Snowflake until a successful writer save.
    // In particular, never stamp it with the browser's clock here.

    // Use client-side calculations for instant updates (no API call needed)
    if (calcTriggerFields.includes(field)) {
      // Apply calculations using preloaded NPN data and current loans (for B Note A+B balance)
      const npn = enrichmentData?.npn || [];

      setRowData(prevData => {
        const nextLoans = prevData.map(row =>
          row.CUSIP === cusip ? { ...row, ...data, [field]: newValue } : row
        );
        return prevData.map(row =>
          row.CUSIP === cusip
            ? {
                ...applyCalculations({ ...row, ...data, [field]: newValue }, npn, nextLoans),
                [field]: newValue,
                MODIFIED_DATE: row.MODIFIED_DATE,
              }
            : row
        );
      });

      if (api) {
        api.refreshCells({ force: true, rowNodes: [params.node] });
      }
    } else {
      // Keep React state in sync with AG Grid's data mutation; the loaded
      // token is not a derived field and remains unchanged.
      setRowData(prevData =>
        prevData.map(row => row.CUSIP === cusip ? { ...row, [field]: newValue } : row)
      );
    }
  }, [trackChange, enrichmentData]);

  const handleSaveChanges = useCallback(async () => {
    const modifiedRows = getChangedData(rowData);

    if (modifiedRows.length === 0) {
      showNotification('No changes to save', 'info');
      return;
    }

    // A placeholder new_row_* key (an upload row without a CUSIP) can never
    // become a real loan from the grid, so it is left out of the batch (and
    // out of validation) and stays on screen; the other edits still save.
    const temporaryRows = modifiedRows.filter(row => hasTemporaryCusip([row]));
    const rowsToSave = modifiedRows.filter(row => !hasTemporaryCusip([row]));

    const errors = validateCREList(rowsToSave);
    if (errors.length > 0) {
      setValidationErrors(formatValidationErrorsForDisplay(errors));
      setValidationErrorModalOpen(true);
      return;
    }

    if (temporaryRows.length > 0) {
      showNotification(
        `${temporaryRows.length} row(s) without a CUSIP were not saved: add a CUSIP ` +
        'column to the upload, or use Add Security',
        'warning'
      );
    }
    if (rowsToSave.length === 0) return;

    try {
      setIsSaving(true);
      // Tokens come from the server-loaded baseline (or null for a new key),
      // not from a grid/upload row. The row itself supplies only data fields.
      const formattedEntries = rowsToSave.map(row => creWriteRow(
        row,
        isNewRow(row) ? { MODIFIED_DATE: null } : row,
      ));
      const response = await saveCRELoan(formattedEntries);
      const savedKeys = new Set(rowsToSave.map(row => String(row.CUSIP)));
      const modifiedDate = response.data.modified_date;
      setRowData(rows => rows.map(row => savedKeys.has(String(row.CUSIP))
        ? savedCREToken(row, modifiedDate)
        : row));
      clearChanges(Array.from(savedKeys));
      savedKeys.forEach(key => pendingModalRef.current.delete(key));
      // Keep any edits that were not part of this save (none normally).
      await loadCRELoans(true, true);
      showNotification(`Successfully saved ${rowsToSave.length} entries`, 'success');
    } catch (err) {
      console.error('Error saving changes:', err);
      await handleWriteFailure(err, 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, [rowData, getChangedData, loadCRELoans, showNotification, clearChanges, isNewRow, handleWriteFailure]);

  const handleAddNewLoan = useCallback(() => {
    setSelectedPropertyType(null);
    setEditingLoan(null);
    setLoanModalOpen(true);
  }, []);

  const handleExportExcel = useCallback(async () => {
    // Export selected rows if any, otherwise export all displayed rows
    const selectedRows = gridRef.current?.api?.getSelectedRows() || [];
    const exportData = selectedRows.length > 0
      ? selectedRows
      : (gridRef.current?.getDisplayedRowData?.() || rowData);

    if (exportData.length === 0) {
      showNotification('No data to export', 'warning');
      return;
    }

    // Build column definitions from creFields for export
    const columns = Object.entries(creFields)
      .filter(([key]) => key !== 'EDIT')
      .map(([key, config]) => ({ field: key, headerName: config.name }));

    await exportToExcel({
      data: exportData,
      columns,
      fileName: `CRE_Loans_${dayjs().format('YYYY-MM-DD')}.xlsx`,
      sheetName: 'CRE Loans',
    });

    const message = selectedRows.length > 0
      ? `Exported ${exportData.length} selected rows`
      : `Exported all ${exportData.length} rows`;
    showNotification(message, 'success');
  }, [rowData, showNotification]);

  const handleBulkEdit = useCallback(() => {
    // Get selected rows directly from AG Grid API
    const selectedRowsData = gridRef.current?.api?.getSelectedRows() || [];
    if (selectedRowsData.length === 0) {
      showNotification('Please select rows to bulk edit', 'warning');
      return;
    }
    setSelectedRowsForBulkEdit(selectedRowsData);
    setBulkEditModalOpen(true);
  }, [showNotification]);

  const handleBulkSave = useCallback(async (bulkUpdatedRows) => {
    try {
      setIsLoading(true);

      const npn = enrichmentData?.npn || [];
      const updatedMap = new Map(bulkUpdatedRows.map(row => [row.CUSIP, row]));
      const allLoansForCalc = rowData.map(row => updatedMap.get(row.CUSIP) || row);
      const calculatedRows = bulkUpdatedRows.map(row => applyCalculations(row, npn, allLoansForCalc));

      setRowData(prevData =>
        prevData.map(row => {
          const updatedRow = calculatedRows.find(ur => ur.CUSIP === row.CUSIP);
          return updatedRow ? updatedRow : row;
        })
      );

      gridRef.current?.api?.refreshCells({ force: true });

      calculatedRows.forEach(row => {
        const originalRow = rowData.find(r => r.CUSIP === row.CUSIP);
        if (originalRow) {
          Object.keys(row).forEach(key => {
            if (row[key] !== originalRow[key]) {
              trackChange(row, key, originalRow[key], row[key]);
            }
          });
        }
      });

      showNotification(`Successfully updated ${bulkUpdatedRows.length} loan(s)`, 'success');
      setBulkEditModalOpen(false);
      setSelectedRowsForBulkEdit([]);
    } catch (err) {
      console.error('Bulk edit failed:', err);
      showNotification('Failed to apply bulk edit', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [rowData, enrichmentData, showNotification, trackChange]);

  const handleDateChange = useCallback((newDate) => {
    if (newDate && newDate.isValid()) {
      setSelectedDate(newDate);
    }
  }, []);


  // === CUSTOM TOOLBAR BUTTONS ===
  // Both of these write: Bulk Edit saves through /save-cre, Refresh Cache posts /refresh-cache, and
  // both are gated on cre.loans:write. The permission reason wins over the descriptive tooltip --
  // with no grant, knowing what the button would have done is not the useful information.
  const customToolbarButtons = useMemo(() => [
    ...(!isPreviousPeriod ? [{
      label: 'Bulk Edit',
      onClick: handleBulkEdit,
      disabled: !canWriteCre || isLoading,
      startIcon: <EditIcon />,
      tooltip: !canWriteCre ? NO_CRE_WRITE_MESSAGE : 'Edit multiple selected rows at once'
    }] : []),
    {
      label: 'Refresh Cache',
      onClick: handleRefreshCache,
      disabled: !canWriteCre || isLoading || isEnrichmentLoading,
      startIcon: <CachedIcon />,
      tooltip: !canWriteCre
        ? NO_CRE_WRITE_MESSAGE
        : 'Force refresh reference data (locations, ClearWater, NCREIF) from database'
    }
  ], [isPreviousPeriod, handleBulkEdit, handleRefreshCache, isLoading, isEnrichmentLoading, canWriteCre]);

  // === COLUMN DEFINITIONS ===
  // Note: the row-select checkbox column is rendered by PamGrid's `selectable`
  // prop (v32.2+ rowSelection.checkboxes), not declared here.
  const columnDefs = useMemo(() => {
    const columns = [];

    // Preferred first columns in display order
    const firstColumns = [
      'CUSIP', 'SERVICER_NUMBER', 'LOAN_NAME', 'PROPERTY_NAME',
      'ENTITY', 'CURRENT_BALANCE', 'MATURITY', 'INTEREST_RATE', 'RISK_RATING_CURRENT'
    ];
    const firstColumnsSet = new Set(firstColumns);
    const orderedEntries = [
      ...firstColumns.filter(key => creFields[key]).map(key => [key, creFields[key]]),
      ...Object.entries(creFields).filter(([key]) => key !== 'EDIT' && !firstColumnsSet.has(key))
    ];

    orderedEntries.forEach(([fieldKey, fieldConfig]) => {

      const column = {
        headerName: fieldConfig.name,
        field: fieldKey,
        editable: !isPreviousPeriod && fieldConfig.editable !== false,
        sortable: true,
        filter: true,
        resizable: true
      };

      if (fieldConfig.fixed) {
        column.pinned = fieldConfig.fixed;
      }

      if (fieldConfig.datatype === 'numeric') {
        column.filter = 'agNumberColumnFilter';
        // Include blank cells in equals/notEqual/lessThan/greaterThan results so a
        // default filter like `STATUS != 6` doesn't silently hide newly-uploaded
        // rows that haven't been assigned a value yet.
        column.filterParams = {
          includeBlanksInEquals: true,
          includeBlanksInNotEqual: true,
          includeBlanksInLessThan: true,
          includeBlanksInGreaterThan: true,
          includeBlanksInRange: true,
        };
        if (fieldConfig.subtype === 'percent') {
          column.valueFormatter = (params) => {
            if (params.value === null || params.value === undefined || params.value === '') return '';
            return (parseFloat(params.value) * 100).toFixed(2) + '%';
          };
        } else if (fieldConfig.subtype === 'pct') {
          column.valueFormatter = (params) => {
            if (params.value === null || params.value === undefined || params.value === '') return '';
            return parseFloat(params.value).toFixed(2) + '%';
          };
        } else if (fieldConfig.subtype === 'bps') {
          column.valueFormatter = (params) => {
            if (params.value === null || params.value === undefined || params.value === '') return '';
            return parseFloat(params.value).toFixed(0) + ' bps';
          };
        } else if (fieldConfig.subtype === 'int') {
          column.valueFormatter = createNumberFormatter(0);
        } else if (fieldConfig.subtype !== 'no-commas') {
          column.valueFormatter = createNumberFormatter(2);
        }

        // Add numeric cell editor for editable numeric fields without choices
        if (column.editable && !fieldConfig.choices) {
          const precision = fieldConfig.subtype === 'percent' ? 6
            : fieldConfig.subtype === 'int' ? 0
            : 2;
          column.cellEditor = 'agNumberCellEditor';
          column.cellEditorParams = { precision };
          column.valueParser = (params) => {
            if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
              return params.oldValue;
            }
            const val = Number(params.newValue);
            return isNaN(val) ? params.oldValue : val;
          };
        }
      }

      if (fieldConfig.datatype === 'date') {
        column.filter = 'agDateColumnFilter';
        column.cellRenderer = DateRenderer;
        if (column.editable) {
          column.cellEditor = 'agDateStringCellEditor';
        }
      }

      if (fieldConfig.datatype === 'datetime') {
        column.cellRenderer = DateTimeRenderer;
      }

      if (fieldConfig.datatype === 'boolean') {
        column.cellRenderer = BooleanRenderer;
        column.valueGetter = (params) => {
          const v = params.data?.[fieldKey];
          if (v === true || v === 'Y' || v === 'Yes' || v === 1 || v === '1') return true;
          if (v === false || v === 'N' || v === 'No' || v === 0 || v === '0') return false;
          return v;
        };
        column.cellEditor = 'agSelectCellEditor';
        column.cellEditorParams = { values: ['Y', 'N', ''] };
        column.valueSetter = (params) => {
          const val = params.newValue;
          const boolVal = val === 'Y' ? true : val === 'N' ? false : val;
          if (params.data[fieldKey] === boolVal) return false;
          params.data[fieldKey] = boolVal;
          return true;
        };
      }

      if (fieldConfig.choices && Array.isArray(fieldConfig.choices)) {
        if (fieldConfig.choiceLabels) {
          column.valueFormatter = (params) => getChoiceDisplayLabel(fieldKey, params.value);
          column.cellEditor = 'agRichSelectCellEditor';
          column.cellEditorParams = {
            values: ['', ...fieldConfig.choices],
            formatValue: (value) => getChoiceDisplayLabel(fieldKey, value),
          };
        } else {
          column.cellEditor = 'agSelectCellEditor';
          column.cellEditorParams = { values: ['', ...fieldConfig.choices] };
        }
      } else if (fieldConfig.choices && typeof fieldConfig.choices === 'object') {
        column.cellEditor = 'agSelectCellEditor';
        column.cellEditorParams = (params) => ({
          values: ['', ...getRowFieldChoices(fieldKey, params.data)],
        });
      }

      columns.push(column);
    });

    return columns;
  }, [isPreviousPeriod]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 100,
    cellDataType: false,
  }), []);

  // === LEGEND ITEMS ===
  const legendItems = useMemo(() => [
    { color: 'rgba(255, 200, 100, 0.3)', border: '1px solid rgba(255, 165, 0, 0.4)', label: 'New rows (unsaved)' },
    { color: 'rgba(255, 165, 0, 0.2)', border: '1px solid rgba(255, 165, 0, 0.4)', label: 'Modified rows' }
  ], []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Date Picker for cutoff date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Cutoff Date:
        </Typography>
        <DatePicker
          value={selectedDate}
          onChange={handleDateChange}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 180 }
            }
          }}
          maxDate={dayjs()}
        />
        {isPreviousPeriod && (
          <Typography variant="body2" sx={{ ml: 2, color: 'warning.main', fontWeight: 'bold' }}>
            Read-Only (Historical)
          </Typography>
        )}
      </Box>

      {/* Error display */}
      {error && typeof error === 'string' && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {/* PamGrid with integrated toolbar */}
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}

        // Toolbar
        title="CRE Loans"
        showToolbar={true}

        // Upload
        showUpload={!isPreviousPeriod}
        onUpload={handleUpload}
        uploadLabel="Upload"
        uploadTooltip="Upload Excel or CSV file with CRE loan data"

        // Add Row
        showAddRow={!isPreviousPeriod}
        onAddRow={handleAddNewLoan}
        addRowLabel="Add Security"

        // Save
        showSave={!isPreviousPeriod}
        onSave={handleSaveChanges}
        changedRowCount={Object.keys(updatedRows).length + rowData.filter(r => isNewRow(r)).length}
        isSaving={isSaving}

        // Export
        showExport={true}
        onExport={handleExportExcel}
        exportLabel="Export Excel"

        // Custom toolbar buttons
        customToolbarButtons={customToolbarButtons}
        customToolbarComponents={gridConfigComponents}

        // Quick filter
        showQuickFilter={true}
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        quickFilterPlaceholder="Quick Filter CRE Loans..."

        // Sizing
        height="calc(100vh - 280px)"
        rowHeight={32}

        // Use AG Grid's native selection (faster than PamGrid's custom selection)
        getRowId={getRowId}
        selectable={{ selectAll: 'filtered' }}
        modules={[RowSelectionModule, SelectEditorModule, DateEditorModule, RichSelectModule]}

        // Actions column
        showActions={true}
        /*
         * 'edit' and 'duplicate' are passed as OBJECTS rather than the usual string keys, purely
         * so their tooltip can explain a permission denial. PamGrid takes a tooltip from an object
         * action but uses its own fixed text ("Edit", "Duplicate") for string keys, and a greyed
         * icon with no reason is a dead end for the user.
         *
         * onClick is deliberately omitted: PamGrid falls back to the onEdit / onDuplicate props
         * below, so the existing wiring is untouched. icon and color mirror core's defaults -- the
         * cost of this approach is that a future change to those defaults will not reach here.
         *
         * 'history' stays a string: it is a read, never gated, and core's "View History" is right.
         */
        actions={isPreviousPeriod ? ['history'] : [
          {
            key: 'edit',
            icon: EditIcon,
            color: 'primary',
            tooltip: canWriteCre ? 'Edit' : NO_CRE_WRITE_MESSAGE,
          },
          {
            key: 'duplicate',
            icon: DuplicateIcon,
            color: 'primary',
            tooltip: canWriteCre ? 'Duplicate' : NO_CRE_WRITE_MESSAGE,
          },
          'history',
        ]}
        isActionDisabled={(action) => !canWriteCre && action !== 'history'}
        onEdit={handleEditLoan}
        onDuplicate={handleDuplicateLoan}
        onHistory={handleViewHistory}

        // Change tracking
        updatedRows={updatedRows}
        isNewRow={isNewRow}

        // Default column definition
        defaultColDef={defaultColDef}

        // State
        isLoading={isLoading || isSaving}
        loadingMessage={isLoading ? 'Loading CRE loans...' : isSaving ? 'Saving...' : 'Processing...'}

        // Callbacks
        onCellValueChanged={handleCellValueChanged}
        onGridReady={handleGridReady}
        // Mirror filter/column/sort gestures into the local stash so unsaved
        // layout tweaks survive a page refresh. PamGrid surfaces
        // onFilterChanged at the top level; the column/sort events ride
        // through agGridProps as the stashBindings bundle.
        onFilterChanged={gridConfigs.onGridStateChanged}

        // Legend
        showLegend={true}
        legendItems={legendItems}

        // Additional AG Grid props (PamGrid already configures v32.2+
        // rowSelection / enableCellTextSelection / animateRows / undoRedo,
        // so no overrides needed here). Merge in the grid-config stash
        // bindings (column move/resize/visible/pin/sort).
        agGridProps={{ ...gridConfigs.stashBindings }}

        undoRedoCellEditingLimit={20}
      />

      {/* Modals */}
      <LoanModal
        open={loanModalOpen}
        onClose={() => {
          setLoanModalOpen(false);
          setEditingLoan(null);
          setDuplicateTemplate(null);
          setSelectedPropertyType(null);
        }}
        onSave={editingLoan ? handleSaveEditedLoan : handleSaveNewLoan}
        propertyType={selectedPropertyType}
        userId={userId}
        loanData={editingLoan}
        template={duplicateTemplate}
        existingLoans={rowData}
      />

      <HistoryModal
        open={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false);
          setSelectedLoanForHistory(null);
        }}
        loanData={selectedLoanForHistory}
      />

      <ValidationErrorModal
        open={validationErrorModalOpen}
        onClose={() => setValidationErrorModalOpen(false)}
        errors={validationErrors}
        title="Validation Errors"
        subtitle="The following entries have validation errors and cannot be saved:"
      />

      <BulkEditModal
        open={bulkEditModalOpen}
        onClose={() => {
          setBulkEditModalOpen(false);
          setSelectedRowsForBulkEdit([]);
        }}
        selectedRows={selectedRowsForBulkEdit}
        onSave={handleBulkSave}
      />
    </Box>
    </LocalizationProvider>
  );
};

export default CREGrid;
